import React, { useCallback, useEffect, useState } from "react";
import { Divider, Modal } from "antd";
import {
  addJob,
  clearQueueArchive,
  clearQueueWaiting,
  removeJob,
} from "api/Reviews";
import { tasks, Task } from "./tasks";
import {
  ErrorCard,
  JobsTabs,
  TasksTable,
  QueuesCard,
  StockManagementControls,
} from "./components";
import { useJobsData, useTableColumns, JobsTabStatusKey } from "./hooks";
import { Job } from "api/Reviews";
import { TABS_CONFIG } from "./config";
import LastReviewManager from "../LastReviewManager";

const DelayJobs = () => {
  const {
    jobStatus,
    loading,
    error,
    lastUpdate,
    autoRefresh,
    setAutoRefresh,
    lastRunTimes,
    fetchData,
    fetchJobsList,
    queues,
    workers,
    workersError,
    handlePauseWorker,
    handleResumeWorker,
  } = useJobsData();

  const [activeTabKey, setActiveTabKey] = useState("1");
  const [activeTabStatusKey, setActiveTabStatusKey] =
    useState<JobsTabStatusKey>("waiting");
  const [currentPage, setCurrentPage] = useState(1);
  const [jobsList, setJobsList] = useState<Job[]>([]);
  const [jobsListTotal, setJobsListTotal] = useState(0);
  const [jobsListLoading, setJobsListLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setJobsListLoading(true);
    fetchJobsList(activeTabStatusKey, currentPage).then((res) => {
      if (!cancelled) {
        setJobsList(res.jobs);
        setJobsListTotal(res.total);
        setJobsListLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [activeTabStatusKey, currentPage, fetchJobsList]);

  const handleTabChange = useCallback((key: string) => {
    setActiveTabKey(key);
    const tab = TABS_CONFIG.find((t) => t.key === key);
    if (tab) setActiveTabStatusKey(tab.statusKey);
    setCurrentPage(1);
  }, []);

  const handleTriggerTask = (task: Task) => {
    addJob({ name: task.jobName, data: task.jobData || {} });
    // Можно добавить уведомление
  };

  const handleRemoveJob = useCallback(
    (job: { id: string; name?: string; queue?: string }): Promise<void> => {
      if (!job.queue) return Promise.resolve();
      return new Promise((resolve) => {
        Modal.confirm({
          title: "Удалить задачу?",
          content: `Задача ${job.name || job.id} будет удалена из очереди. Удалить?`,
          okText: "Удалить",
          cancelText: "Отмена",
          okButtonProps: { danger: true },
          onOk: async () => {
            const res = await removeJob(job.queue!, job.id);
            if (res.error) console.error(res.error);
            else {
              await fetchData(false);
              const listRes = await fetchJobsList(activeTabStatusKey, currentPage);
              setJobsList(listRes.jobs);
              setJobsListTotal(listRes.total);
            }
            resolve();
          },
          onCancel: () => resolve(),
        });
      });
    },
    [fetchData, fetchJobsList, activeTabStatusKey, currentPage]
  );

  const { waitingJobsColumns, delayedJobsColumns, completedJobsColumns, tasksColumns } =
    useTableColumns(
      lastRunTimes,
      handleTriggerTask,
      handleRemoveJob,
    );

  return (
    <>
      <ErrorCard error={error} />

      <QueuesCard
        queues={queues}
        workers={workers}
        workersError={workersError}
        loading={loading}
        autoRefresh={autoRefresh}
        lastUpdate={lastUpdate}
        onAutoRefreshToggle={() => setAutoRefresh(!autoRefresh)}
        onRefresh={() => fetchData(true)}
        onClearQueueArchive={async (queueName) => {
          await clearQueueArchive(queueName);
          fetchData(true);
        }}
        onClearQueueWaiting={async (queueName) => {
          await clearQueueWaiting(queueName);
          fetchData(true);
        }}
        onPause={handlePauseWorker}
        onResume={handleResumeWorker}
      />

      <StockManagementControls />

      <JobsTabs
        jobStatus={jobStatus}
        activeTabKey={activeTabKey}
        onTabChange={handleTabChange}
        jobsList={jobsList}
        jobsListTotal={jobsListTotal}
        jobsListLoading={jobsListLoading}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        activeTabStatusKey={activeTabStatusKey}
        waitingJobsColumns={waitingJobsColumns}
        delayedJobsColumns={delayedJobsColumns}
        completedJobsColumns={completedJobsColumns}
      />

      <Divider />

      <TasksTable tasks={tasks} tasksColumns={tasksColumns} />

      <Divider />

      <LastReviewManager />
    </>
  );
};

export default DelayJobs;
